import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Pencil } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import OverviewTab from './AccountDetail/OverviewTab';
import ContactsTab from './AccountDetail/ContactsTab';
import TimelineTab from './AccountDetail/TimelineTab';
import CommunicationsTab from './AccountDetail/CommunicationsTab';
import TasksTab from './AccountDetail/TasksTab';
import ProjectsTab from './AccountDetail/ProjectsTab';
import FinancialTab from './AccountDetail/FinancialTab';
import MediaTab from './AccountDetail/MediaTab';
import AccountOutreachPanel from '../../components/admin/AccountOutreachPanel';
import RunAuditButton from '../../components/admin/RunAuditButton';
import AccountForm from '../../components/admin/AccountForm';
import AuditsTab from './AccountDetail/AuditsTab';
import AccountNotificationRouting from '../../components/admin/AccountNotificationRouting';
import BackLink from '../../components/admin/BackLink';
import SupportTeamPanel from '../../components/admin/SupportTeamPanel';
import DistrictMembersPanel from '../../components/admin/DistrictMembersPanel';
import InternalNotesPanel from '../../components/admin/InternalNotesPanel';
import AccountAnalyticsTab from '../../components/admin/AccountAnalyticsTab';
import ActivityFeed from '../../components/admin/ActivityFeed';
import UsersTab from './AccountDetail/UsersTab';

function tabsForRole(role) {
  const inline = [
    { key: 'overview', label: 'Overview' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'projects', label: 'Projects' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'audits', label: 'Audits' },
    { key: 'outreach', label: 'Outreach' },
  ];

  if (role !== 'nexsav_admin' && role !== 'ops') {
    return { inline, moreGroups: [] };
  }

  // Communications (email + calls + visits in one list) sits between Contacts
  // and Timeline for staff. `dot` marks the tab when the school wrote last.
  inline.splice(2, 0, { key: 'communications', label: 'Communications', dot: 'needsReply' });

  const moreGroups = [
    {
      label: 'Records & insights',
      items: [
        { key: 'financial', label: 'Financial' },
        { key: 'media', label: 'Media' },
        { key: 'notes', label: 'Notes' },
        { key: 'analytics', label: 'Analytics' },
        { key: 'activity', label: 'Activity' },
      ],
    },
    {
      label: 'Configuration',
      items: [
        { key: 'users', label: 'Users' },
        { key: 'support-team', label: 'Support team' },
        { key: 'notifications', label: 'Notifications' },
      ],
    },
  ];

  return { inline, moreGroups };
}

export default function AccountDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [editOpen, setEditOpen] = useState(false); // rename / site address / zone, in place
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');

  const { inline, moreGroups } = tabsForRole(user?.role);
  const moreItems = moreGroups.flatMap((g) => g.items);
  const tabKeys = new Set([...inline, ...moreItems].map((t) => t.key));
  const tab = urlTab && tabKeys.has(urlTab) ? urlTab : 'overview';
  const moreActive = moreItems.some((t) => t.key === tab);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'overview') next.delete('tab');
    else next.set('tab', key);
    setSearchParams(next, { replace: true });
    setMoreOpen(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/accounts/${id}`);
      setAccount(res.account);
    } catch (err) {
      setError(err.message || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // After an audit: refresh the account's score quietly (no loading flash) and
  // tell the Audits tab to reload its history.
  const [auditsVersion, setAuditsVersion] = useState(0);
  const onAuditCompleted = useCallback(async () => {
    setAuditsVersion((v) => v + 1);
    try {
      const res = await api.get(`/api/admin/accounts/${id}`);
      setAccount(res.account);
    } catch { /* the next full load picks it up */ }
  }, [id]);

  // Amber dot on the Communications tab when the school wrote last and nobody
  // answered. Cheap probe (no sync, one conversation); the tab itself reports
  // the live summary once opened so the dot clears without a reload.
  const isStaff = user?.role === 'nexsav_admin' || user?.role === 'ops';
  const [needsReply, setNeedsReply] = useState(0);
  useEffect(() => {
    if (!isStaff || !id) return undefined;
    let alive = true;
    const p = api.get(`/api/admin/accounts/${id}/communications?limit=1&sync=0`);
    if (!p || typeof p.then !== 'function') return undefined;
    p.then((d) => { if (alive) setNeedsReply(Number(d?.summary?.needsReply) || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [id, isStaff]);
  const onCommsSummary = useCallback((summary) => {
    if (summary) setNeedsReply(Number(summary.needsReply) || 0);
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <BackLink to="/admin/accounts" className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to accounts
      </BackLink>

      {loading && !account && (
        <div className="animate-pulse h-20 rounded-xl bg-white/[0.03]" />
      )}
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}

      {account && (
        <>
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight">{account.name}</h1>
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05] transition-colors"
                  title="Edit name, site address or zone"
                  aria-label="Edit account"
                  data-testid="edit-account"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[12px] text-gray-500 mt-1 font-mono flex items-center gap-3 flex-wrap">
                <span>{account.slug}</span>
                {account.typeSchoolDbn && <span>· DBN {account.typeSchoolDbn}</span>}
                {account.type && <span className="uppercase tracking-wider text-gray-600">· {account.type}</span>}
              </div>
            </div>
            <RunAuditButton account={{ id: account.id, siteUrl: account.siteUrl }} onCompleted={onAuditCompleted} />
          </div>
          <AccountForm open={editOpen} onClose={() => setEditOpen(false)} onSaved={(saved) => setAccount((prev) => ({ ...prev, ...saved }))} initial={account} />

          <div className="flex flex-wrap items-center gap-1 mb-6 border-b border-white/[0.05]">
            {inline.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 -mb-px border-b-2 text-[13px] transition-colors ${
                  tab === t.key
                    ? 'border-[#B0FC38] text-white'
                    : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
              >
                {t.label}
                {t.dot === 'needsReply' && needsReply > 0 && (
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 ml-1.5 align-middle"
                    data-testid="tab-dot-communications"
                    aria-label={`${needsReply} awaiting reply`}
                    role="img"
                  />
                )}
              </button>
            ))}
            {moreGroups.length > 0 && (
              <>
                <div className="w-px h-4 bg-white/[0.08] mx-1.5" aria-hidden="true" />
                <div ref={moreRef} className="relative">
                  <button
                    onClick={() => setMoreOpen((o) => !o)}
                    className={`px-4 py-2 -mb-px border-b-2 text-[13px] transition-colors inline-flex items-center gap-1 ${
                      moreActive
                        ? 'border-[#B0FC38] text-white'
                        : 'border-transparent text-gray-500 hover:text-gray-300'
                    }`}
                    aria-haspopup="menu"
                    aria-expanded={moreOpen}
                  >
                    More
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {moreOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 top-full mt-1 min-w-[200px] rounded-lg bg-zinc-950 border border-white/[0.08] shadow-xl shadow-black/40 py-1 z-20"
                    >
                      {moreGroups.map((g, gi) => (
                        <div key={g.label}>
                          {gi > 0 && <div className="border-t border-white/[0.06] my-1" />}
                          <div className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-gray-600">
                            {g.label}
                          </div>
                          {g.items.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => setTab(t.key)}
                              role="menuitem"
                              className={`w-full text-left px-3 py-1.5 text-[13px] transition-colors ${
                                tab === t.key
                                  ? 'bg-[#B0FC38]/[0.12] text-white'
                                  : 'text-gray-300 hover:bg-white/[0.04] hover:text-white'
                              }`}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {tab === 'overview' && (
            <>
              <OverviewTab account={account} role={user?.role} onChange={setAccount} onDeleted={() => navigate('/admin/accounts')} />
              {account.type === 'district' && (
                <div className="mt-6">
                  <DistrictMembersPanel districtAccountId={account.id} />
                </div>
              )}
            </>
          )}
          {tab === 'contacts' && <ContactsTab account={account} />}
          {tab === 'communications' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <CommunicationsTab account={account} onSummary={onCommsSummary} />
          )}
          {tab === 'timeline' && <TimelineTab account={account} />}
          {tab === 'projects' && <ProjectsTab account={account} />}
          {tab === 'tasks' && <TasksTab account={account} />}
          {tab === 'outreach' && <AccountOutreachPanel account={account} />}
          {tab === 'audits' && <AuditsTab account={account} refreshKey={auditsVersion} onAuditCompleted={onAuditCompleted} />}
          {tab === 'users' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <UsersTab account={account} />
          )}
          {tab === 'support-team' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <SupportTeamPanel accountId={account.id} />
          )}
          {tab === 'notifications' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <AccountNotificationRouting accountId={account.id} />
          )}
          {tab === 'notes' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <InternalNotesPanel accountId={account.id} />
          )}
          {tab === 'analytics' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <AccountAnalyticsTab accountId={account.id} />
          )}
          {tab === 'activity' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <ActivityFeed apiPath={`/api/admin/accounts/${account.id}/activity`} />
          )}
          {tab === 'financial' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && <FinancialTab account={account} />}
          {tab === 'media' && (user?.role === 'nexsav_admin' || user?.role === 'ops') && (
            <MediaTab accountId={account.id} accountName={account.name} />
          )}
        </>
      )}
    </div>
  );
}
